<template>
  <v-container
    fluid
    :style="{height:'100%', 'background-color':pageElements[$route.name]? pageElements[$route.name].color: '' }"
  >
    <task-dialog :openAtStart="false" />

    <v-layout row wrap>
      <v-flex xs12>
        <v-img
          :src="pageElements[$route.name]?pageElements[$route.name].img:''"
          height="100px"
          contain
        ></v-img>
      </v-flex>

      <v-flex sm12 md9>
        <v-card ma-5 height="100%" min-height="550px">
          <v-card-title>
            <span class="headline">Geben Sie Ihren zu klassifizierenden Text ein</span>
            <v-spacer></v-spacer>
          </v-card-title>

          <v-card-text>
            <v-form v-on:submit.prevent="classify">
              <v-text-field
                v-model="text"
                :append-outer-icon="'send'"
                :prepend-icon="icon"
                box
                clear-icon="close"
                clearable
                label="Text"
                type="text"
                @click:append-outer="classify"
                @click:clear="clearText"
                @input="debounceClassify"
              >
                <v-progress-linear
                  v-if="loading"
                  slot="progress"
                  color="warning"
                  :indeterminate="true"
                ></v-progress-linear>

                <v-progress-linear v-else slot="progress" color="success" :value="100" height="7"></v-progress-linear>
              </v-text-field>
            </v-form>
          </v-card-text>

          <v-card-text>
            <v-layout
              v-if="loading"
              slot="placeholder"
              fill-height
              align-center
              justify-center
              ma-0
            >
              <v-progress-circular indeterminate color="grey"></v-progress-circular>
            </v-layout>

            <v-layout column v-else-if="taskData" align-center>
              <v-flex xs12 v-if="$route.name !== 'exp_hate_toxicity'">
                <v-chip
                  outline
                  v-for="(label,classifierName,i) in classificationLabels"
                  :color="classifierColor(classifierName )"
                  :key="i"
                >
                  <v-icon
                    left
                    :color="classifierColor(classifierName )"
                  >{{classifierIcon(classifierName, label)}}</v-icon>
                  {{label}}
                </v-chip>
                <v-chip v-if="false" outline>{{taskData.language}}</v-chip>
              </v-flex>
              <v-flex xs12>
                <task :taskData="taskData" :taskIndex="0" :context="'interact-pj'"></task>
              </v-flex>
            </v-layout>

            <v-layout column align-end>
              <v-flex xs12>
                <v-menu>
                  <template v-slot:activator="{ on }">
                    <v-tooltip top>
                      <v-btn
                        slot="activator"
                        dark
                        :color="pageElements[$route.name]?pageElements[$route.name].color:''"
                        v-on="on"
                      >Ich bin mit der Vorhersage nicht einverstanden</v-btn>
                      <span>Klicken Sie hier, um einen Fehler zu melden</span>
                    </v-tooltip>
                    <v-snackbar
                      color="success"
                      v-model="snackbar"
                      :timeout="2000"
                    >Vielen Dank für Ihre Rückmeldung!</v-snackbar>
                  </template>
                  <v-list>
                    <v-list-tile
                      v-for="(predictionclass, i) in predictionClasses"
                      :key="i"
                      @click="sendFeedback(predictionclass)"
                    >
                      <v-list-tile-title>{{ predictionclass.title }}</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex sm12 md3>
        <v-card ma-5 height="100%">
          <infobox
            :explanation="pageElements[$route.name]?pageElements[$route.name].explanation:''"
            :infos="interactInfos"
          ></infobox>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Task from "@/components/common/Task.vue";
import { mapGetters, mapActions } from "vuex";
import TaskDialog from "@/components/common/TaskDialog.vue";
import _ from "lodash";
import Particles from "@/components/common/Particles.vue";
import Infobox from "@/components/common/Infobox.vue";

export default {
  name: "interact-pj",
  components: { Task, TaskDialog, Particles, Infobox },
  props: {
    // classifierNames: { type: Array, default: null }
  },
  data() {
    return {
      language: "German - Swiss German",

      snackbar: false,
      text: "",
      computeAttributions: false,
      addToDataset: false,
      taskData: null,
      loading: false,
      label: null,
      HATE_THRESHOLD: 0.5,
      TOXICITY_THRESHOLD: 0.5,
      HATE_DE_THRESHOLD: 0.65,
      predictionClasses: [
        { label: "profane", title: "Dieser Text ist profan" },
        { label: "hate", title: "Dieser Text ist hasserfüllt" },
        { label: "toxic", title: "Dieser Text ist giftig" },
        { label: "inappropriate", title: "Dieser Text ist unangemessen" }, 
        { label: "plain", title: "Dieser Text ist in Ordnung" },
        { label: "other", title: "Dieser Text fällt in die Kategorie 'Sonstige'" }
      ]
    };
  },
  computed: {
    ...mapGetters("labeling", {
      classifiersConfigs: "classifiersConfigs"
    }),
    ...mapGetters("common", {
      pageElements: "pageElements",
      interactInfos: "interactInfos"
    }),
    languages() {
      return ["German - Swiss German"];
    },
    classifierNames() {
      return ["de_keyboard_prediction", "profanity_ch-de", "hate_de_trans", "hate_de_all", "hate_de_germeval2019"];
    },
    classificationLabels() {
      let label = "clean";
      // Otherwise, let us use the four models and just predict hate if one is above the threshold
      let targetClassifiers = ["hate_de_all", "hate_de_trans", "hate_de_germeval2019"];
      for (const classifierName of targetClassifiers) {
        if (classifierName in this.taskData.predictions) {
          let bestScore = _.maxBy(this.taskData.predictions[classifierName].preds, function(o) {
            return o.score;
          });
          if (bestScore.label === "clean") {
            if (bestScore.score < this.HATE_DE_THRESHOLD) {
              label = "Unangemessener Inhalt von Machine Learning entdeckt";
              break;
            } else {
              label = "In Ordnung";
            }
          } else {
            label = "Unangemessener Inhalt von Machine Learning entdeckt";
            break;
          }
        }
        if (label == "In Ordnung") {
          let best = _.maxBy(this.taskData.predictions["profanity_ch-de"].preds, function(o) {
          return o.score;
          });
          if (best.label === "profane") {
            label = "Unangemessener Inhalt von Profanitätsliste ausgewählt";
          } else {
            label = "In Ordnung";
          }
        }
      }
      return { hate_de: label };
    },
    _() {
      return _;
    },
    icon() {
      return "";
    }
  },
  methods: {
    ...mapActions("labeling", {
      addSwissGermanTexts: "addSwissGermanTexts",
      addTexts: "addTexts",
      classifyTexts: "classifyTexts",
      setClassifierNames: "setClassifierNames"
    }),
    classifierColor(classifierName) {
      let mapper = {
        emotion_ensemble: "indigo",
        profanity_en: "violet",
        hate: "blue",
        hate_de: "blue",
        toxicity: "brown"
      };
      return mapper[classifierName];
    },
    classifierIcon(classifierName, label) {
      let mapper = {
        emotion_ensemble: {
          anger: "mood_bad",
          fear: "mood_bad",
          joy: "sentiment_satisfied_alt",
          love: "favorite",
          neutral: "face",
          sadness: "sentiment_dissatisfied",
          surprise: "sentiment_very_satisfied"
        },
        hate: { clean: "done", hate: "error" },
        toxicity: { safe: "done", toxic: "warning" },
        profanity_en: { clean: "done", profane: "error" },
        hate_de: { clean: "done", hate: "error" }
      };
      return mapper[classifierName][label];
    },
    debounceClassify: _.debounce(function() {
      this.classify();
    }, 500),
    clearText() {
      this.text = "";
    },
    sendFeedback(predictionClass) {
      this.label = predictionClass.label;
      this.addSwissGermanTexts({ 
        text: this.text, 
        collection: this.$route.name,
        label: this.label });
      this.snackbar = true;
    },
    classify() {
      this.setClassifierNames(this.classifierNames);
      let self = this;
      if (!self.text) {
        return;
      }
      self.taskData = null;
      self.loading = true;
      self
        .classifyTexts({
          texts: [self.text],
          computeAttributions: self.computeAttributions
        })
        .then(function(results) {
          self.taskData = results.data.results[0];
          // if we have a set of target classifier names, we filter the task data to that list.
          if (self.classifierNames) {
            let predictions = {};
            for (let classifierName of self.classifierNames) {
              if (classifierName in self.taskData.predictions) {
                predictions[classifierName] = self.taskData.predictions[classifierName];
              }
            }
            self.taskData.predictions = predictions;
          }
          self.loading = false;
          if (self.addToDataset) {
            self.addTexts([self.text]);
          }
        });
    }
  },
  created() {}
};
</script>

<style>
</style>

